// import logo from './logo.svg';
import React, { lazy, Suspense } from 'react';
import './App.css';
import { Box, CircularProgress } from '@mui/material';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import HomePage from './Pages/Home/home';
import ContactPage from './Pages/Contact/ContactPage';
import DocsPage from './Pages/Docs/docs';
import AboutPage from './Pages/About/AboutPage';
import PatternGeneratorPage from './Pages/PatternGenerator/patterngenerator';
import GridEditorPage from './Pages/GridEditor/grideditor';
import ArcadePage from './Pages/Arcade/arcade';
import GraphEditorPage from './Pages/GraphEditor/grapheditor';
import PostPage from './Pages/PostPage/PostPage';
import Game from './Pages/game/Game';
import GamePage from './Pages/Arcade/GamePage';
import GameLeaderboardView from './Pages/Arcade/Dashboard/GameLeaderboardView';
import UserProfileView from './Pages/Arcade/Dashboard/UserProfileView';
import AgentDetailView from './Pages/Arcade/Dashboard/AgentDetailView';
import ReplayPage from './Pages/Arcade/Replay/ReplayPage';
import SpectateSessionPage from './Pages/Arcade/Spectate/SpectateSessionPage';
import Login from './Pages/Auth/Login';
import Register from './Pages/Auth/Register';
import VerifyEmail from './Pages/Auth/VerifyEmail';
import ForgotPassword from './Pages/Auth/ForgotPassword';
import ResetPassword from './Pages/Auth/ResetPassword';
import Account from './Pages/Auth/Account';
import AdminDashboard from './Pages/Admin/AdminDashboard';
import AudioVisualizerPage from './Pages/Art/AudioVisualizer';
import {
    LotkaVolterraPage,
    FisherEquationPage,
    FitzHughNagumoPage,
    HodgkinHuxleyPage,
    SegregationPage,
    EpidemicPage,
    FlockingPage,
} from './Pages/Biological/biologicalModels';
import { AuthProvider } from './Context/AuthContext';
import PageHitTracker from './Components/Analytics/PageHitTracker';

const DigitalTwinsPage = lazy(() => import('./Pages/DigitalTwins/DigitalTwinsPage'));

function LegacyPostRedirect() {
    const { postId } = useParams();
    return <Navigate to={`/docs/post/${postId}`} replace />;
}

function App() {
    return (
        <AuthProvider>
            <PageHitTracker />
            <div className="Box">
                <Box sx={{ width: '100%' }}>
                    <Routes>
                        <Route exact path="/" element={<HomePage />} />
                        <Route exact path="/about" element={<AboutPage />} />
                        <Route exact path="/contact" element={<ContactPage />} />
                        <Route exact path="/docs" element={<DocsPage />} />
                        <Route exact path="/docs/post/:postId" element={<PostPage />} />
                        <Route exact path="/contact/post/:postId" element={<LegacyPostRedirect />} />

                        <Route exact path="/biological/ai-turing-patterns" element={<PatternGeneratorPage />} />
                        <Route exact path="/biological/lotka-volterra" element={<LotkaVolterraPage />} />
                        <Route exact path="/biological/fisher" element={<FisherEquationPage />} />
                        <Route exact path="/biological/fitzHugh-nagumo" element={<FitzHughNagumoPage />} />
                        <Route exact path="/biological/hodgkin-huxley" element={<HodgkinHuxleyPage />} />
                        <Route exact path="/biological/segregation" element={<SegregationPage />} />
                        <Route exact path="/biological/epidemic" element={<EpidemicPage />} />
                        <Route exact path="/biological/flocking" element={<FlockingPage />} />

                        <Route exact path="/robotic/markov-chains" element={<GraphEditorPage />} />
                        <Route exact path="/robotic/mobility-systems" element={<GridEditorPage />} />
                        <Route
                            exact
                            path="/digital_twins"
                            element={
                                <Suspense
                                    fallback={
                                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 20 }}>
                                            <CircularProgress sx={{ color: '#2cbb5d' }} />
                                        </Box>
                                    }
                                >
                                    <DigitalTwinsPage />
                                </Suspense>
                            }
                        />

                        <Route exact path="/art/audio-visualizer" element={<AudioVisualizerPage />} />

                        <Route exact path="/arcade" element={<ArcadePage />} />
                        {/* Legacy /dashboard and /arcade/dashboard → hub (sets Dashboard – EvolveX) */}
                        <Route exact path="/dashboard" element={<Navigate to="/arcade?section=dashboard" replace />} />
                        <Route exact path="/arcade/dashboard" element={<Navigate to="/arcade?section=dashboard" replace />} />
                        <Route exact path="/arcade/dashboard/games/:gameId" element={<GameLeaderboardView />} />
                        <Route exact path="/arcade/dashboard/users/:userId" element={<UserProfileView />} />
                        <Route exact path="/arcade/dashboard/agents/:agentId" element={<AgentDetailView />} />
                        <Route exact path="/arcade/replay/:runId" element={<ReplayPage />} />
                        <Route exact path="/arcade/spectate/:sessionId" element={<SpectateSessionPage />} />
                        <Route exact path="/arcade/:gameId" element={<GamePage />} />
                        <Route exact path="/auth/login" element={<Login />} />
                        <Route exact path="/auth/register" element={<Register />} />
                        <Route exact path="/verify-email" element={<VerifyEmail />} />
                        {/* SPA routes outside /auth so CRA setupProxy does not steal them */}
                        <Route exact path="/forgot-password" element={<ForgotPassword />} />
                        <Route exact path="/reset-password" element={<ResetPassword />} />
                        <Route exact path="/account" element={<Account />} />
                        <Route exact path="/admin" element={<AdminDashboard />} />
                        <Route exact path="/game" element={<Game />} />

                        {/* Legacy route aliases */}
                        <Route exact path="/pattern-generator" element={<Navigate to="/biological/ai-turing-patterns" replace />} />
                        <Route exact path="/grid-editor" element={<Navigate to="/robotic/mobility-systems" replace />} />
                        <Route exact path="/graph-editor" element={<Navigate to="/robotic/markov-chains" replace />} />
                        <Route exact path="/video-generator" element={<Navigate to="/art/audio-visualizer" replace />} />
                        <Route exact path="/text-file-editor" element={<Navigate to="/docs" replace />} />
                        <Route exact path="/discuss" element={<Navigate to="/docs" replace />} />
                    </Routes>
                </Box>
            </div>
        </AuthProvider>
    );
}

export default App;
